import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { formatCents, formatDay, mountainDay } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { useAccessNames } from "./accessNames";

/** What the server's confirm_required answer carries for a date move: its sentence and preview. */
interface MovePreview {
  message: string;
  from_text?: string;
  to_text?: string;
  shift?: string;
  charged?: string;
  notes?: string[];
}

/** Ask without confirm: the server answers confirm_required with the preview (nothing changes yet). */
async function askPreview(send: () => Promise<unknown>, pick: (d: Record<string, unknown>) => Omit<MovePreview, "message">): Promise<MovePreview> {
  try {
    await send();
  } catch (e) {
    if (e instanceof ApiError && e.code === "confirm_required") return { message: e.message, ...pick((e.details ?? {}) as Record<string, unknown>) };
    throw e;
  }
  throw new Error("The server didn't ask for a confirmation, so nothing was changed. Tell crm-billing.");
}

function Preview({ p }: { p: MovePreview }) {
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 rounded-md border p-3 text-sm">
      {p.from_text && (
        <>
          <dt className="text-[var(--color-muted-foreground)]">Now</dt>
          <dd>{p.from_text}</dd>
        </>
      )}
      {p.to_text && (
        <>
          <dt className="text-[var(--color-muted-foreground)]">New date</dt>
          <dd className="font-semibold">
            {p.to_text}
            {p.shift ? <span className="font-normal text-[var(--color-muted-foreground)]"> ({p.shift})</span> : null}
          </dd>
        </>
      )}
      <dt className="text-[var(--color-muted-foreground)]">Charged</dt>
      <dd>{p.charged ?? p.message}</dd>
      {(p.notes ?? []).map((n) => (
        <dd key={n} className="col-start-2 text-xs text-[var(--color-muted-foreground)]">
          {n}
        </dd>
      ))}
    </dl>
  );
}

/** subscriptions_update next_bill_date: earlier or later, always a preview first (Brandon, 2026-11-04). */
export function RenewalDateDialog({ sub, onClose }: { sub: { id: number; next_bill_date: string | null; name: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [day, setDay] = useState(sub.next_bill_date?.slice(0, 10) ?? "");
  const [preview, setPreview] = useState<MovePreview | null>(null);
  const [problem, setProblem] = useState("");
  const send = (confirm: boolean) => api(`/api/v1/subscriptions/${sub.id}`, { method: "PATCH", json: { next_bill_date: day, confirm: confirm || undefined } });
  const review = useMutation({
    mutationFn: () =>
      askPreview(
        () => send(false),
        (d) => ({ from_text: d.renewal_now as string, to_text: d.new_renewal as string, shift: d.shift as string, charged: d.charged as string }),
      ),
    onMutate: () => setProblem(""),
    onSuccess: setPreview,
    onError: (e) => setProblem(e.message),
  });
  const save = useMutation({
    mutationFn: () => send(true),
    onSuccess: () => {
      for (const k of ["subscription", "subscriptions", "billing-detail", "billing-summary", "timeline"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(`Renewal moved to ${formatDay(day)}`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>{preview ? "Check before moving" : `Change the renewal date: ${sub.name}`}</DialogTitle>
        {!preview ? (
          <>
            <DialogDescription>
              Next renewal now: {sub.next_bill_date ? formatDay(sub.next_bill_date) : "none"}. Pick the new date (Mountain Time), earlier or later. Nothing changes until you confirm on the next step.
            </DialogDescription>
            <div className="space-y-1 text-sm">
              <Label htmlFor="rd-day">New renewal date</Label>
              <Input id="rd-day" type="date" min={mountainDay()} value={day} onChange={(e) => setDay(e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <DialogDescription>This is what changes. The member gets no email about it; tell them if they asked by phone.</DialogDescription>
            <Preview p={preview} />
          </>
        )}
        {problem && <p className="text-sm text-[var(--color-danger-fg)]">{problem}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? "Back" : "Cancel"}
          </Button>
          {!preview ? (
            <Button disabled={!day || day === sub.next_bill_date?.slice(0, 10) || review.isPending} onClick={() => review.mutate()}>
              Review
            </Button>
          ) : (
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              Move the renewal
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** orders_move_installment: one payment of a payment plan to another date; the others keep theirs. */
export function MoveInstallmentDialog({ orderId, row, onClose }: { orderId: number; row: { seq: number; due_date: string; amount_cents: number }; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [day, setDay] = useState(row.due_date.slice(0, 10));
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<MovePreview | null>(null);
  const [problem, setProblem] = useState("");
  const send = (confirm: boolean) =>
    api(`/api/v1/orders/${orderId}/installments/move`, { json: { seq: row.seq, due_date: day, reason: reason.trim() || undefined, confirm: confirm || undefined } });
  const review = useMutation({
    mutationFn: () =>
      askPreview(
        () => send(false),
        (d) => ({ from_text: d.due_now as string, to_text: d.new_date as string, shift: d.shift as string, charged: d.charged as string, notes: d.notes as string[] }),
      ),
    onMutate: () => setProblem(""),
    onSuccess: setPreview,
    onError: (e) => setProblem(e.message),
  });
  const save = useMutation({
    mutationFn: () => send(true),
    onSuccess: () => {
      for (const k of ["order", "orders", "billing-detail", "billing-summary", "timeline"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(`Payment ${row.seq} moved to ${formatDay(day)}`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>{preview ? "Check before moving" : `Move payment ${row.seq} (${formatCents(row.amount_cents)})`}</DialogTitle>
        {!preview ? (
          <>
            <DialogDescription>
              Due now: {formatDay(row.due_date)}. Only this payment moves; the others keep their dates. Nothing changes until you confirm on the next step.
            </DialogDescription>
            <div className="grid gap-3 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
              <div className="space-y-1">
                <Label htmlFor="mi-day">New date</Label>
                <Input id="mi-day" type="date" min={mountainDay()} value={day} onChange={(e) => setDay(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mi-reason">Reason (kept on the timeline)</Label>
                <Input id="mi-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Asked by phone: paid on the 15th" />
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogDescription>This is what changes. The customer gets no email about it; tell them if they asked by phone.</DialogDescription>
            <Preview p={preview} />
          </>
        )}
        {problem && <p className="text-sm text-[var(--color-danger-fg)]">{problem}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? "Back" : "Cancel"}
          </Button>
          {!preview ? (
            <Button disabled={!day || day === row.due_date.slice(0, 10) || review.isPending} onClick={() => review.mutate()}>
              Review
            </Button>
          ) : (
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              Move the payment
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** access_revoke by key, with a reason (kept on the grant and the timeline). */
export function RemoveAccessDialog({ contactId, access, onClose }: { contactId: number; access: { key: string; sources: string[] }; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState("");
  const label = useAccessNames()(access.key);
  const fromSubscription = access.sources.includes("subscription");
  const remove = useMutation({
    mutationFn: () => api<{ revoked: number }>(`/api/v1/contacts/${contactId}/access/revoke`, { json: { key: access.key, reason: reason.trim() } }),
    onSuccess: (r) => {
      for (const k of ["billing-detail", "billing-summary", "contact", "timeline", "subscription"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(r.revoked ? `Removed ${label}` : `${label} was already gone`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(500px,calc(100vw-2rem))]">
        <DialogTitle>Remove {label}?</DialogTitle>
        <DialogDescription>
          This takes the access away now, from every source it came from. Nothing is refunded and no email is sent. It is logged on the timeline with your name and reason.
        </DialogDescription>
        {fromSubscription && (
          <p className="rounded-md border p-2 text-xs text-[var(--color-warning-fg)]">
            Part of this access comes from a subscription: its next payment gives it back. To stop it for good, cancel the subscription instead.
          </p>
        )}
        <div className="space-y-1 text-sm">
          <Label htmlFor="ra-reason">Reason (required)</Label>
          <Input id="ra-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Refunded in Keap on Nov 4" />
        </div>
        {problem && <p className="text-sm text-[var(--color-danger-fg)]">{problem}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="destructive" disabled={!reason.trim() || remove.isPending} onClick={() => remove.mutate()}>
            Remove access
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
